'use client';

import { differenceInDays } from 'date-fns';
import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { trpc } from '@/lib/api/trpc';
import { useAccountTimezone } from '@/hooks/use-account-timezone';
import { getDefaultDateRange, getLastYearDateRange } from './utils/datePresets';
import { getDatePresetsForTimezone } from './utils/datePresets';
import { createTimezoneAwareDate } from './utils/timezoneUtils';
import { useUrlSync } from './utils/urlSync';

type DateRange = { from: Date; to: Date };
type StatGranularity = 'daily' | 'weekly' | 'monthly';

interface StatsFiltersContextType {
    selectedDates: DateRange;
    selectDates: (dates: DateRange) => void;
    granularities: StatGranularity[];
    selectedGranularity: StatGranularity;
    selectGranularity: (value: StatGranularity) => void;
    selectedStores: string[];
    selectStores: (stores: string[]) => void;
    selectedTimezone: string;
    selectTimezone: (timezone: string) => void;
    selectedCurrency: string;
    selectCurrency: (currency: string) => void;
    currencies: string[];
    datePresets: { label: string; dateRange: DateRange }[];
}

export const StatsFiltersContext = createContext<StatsFiltersContextType>({} as StatsFiltersContextType);

type DefaultDatePreset = 'standard' | 'lastYear';

export const StatsFiltersProvider: FC<PropsWithChildren<{ defaultDatePreset?: DefaultDatePreset }>> = ({
  children,
  defaultDatePreset = 'standard',
}) => {
    const [searchParams] = useSearchParams();
    const { timezone: accountTimezone, isReady: isAccountTimezoneReady } = useAccountTimezone();

    // Initialize from URL. When the URL doesn't specify a timezone we
    // start from UTC and let the effect below snap to the account's
    // preferred timezone once the tRPC query resolves.
    const initialFrom = searchParams.get('from');
    const initialTo = searchParams.get('to');
    const initialGranularity = (searchParams.get('granularity') as StatGranularity) || 'daily';
    const initialStores = (searchParams.get('stores') || '').split(',').filter(Boolean);
    const urlTimezone = searchParams.get('timezone');
    const initialTimezone = urlTimezone || 'UTC';
    const initialCurrency = searchParams.get('currency') || 'USD';

    const resolveDefaultDates = (timezone: string) =>
      defaultDatePreset === 'lastYear' ? getLastYearDateRange(timezone) : getDefaultDateRange(timezone);

    const [selectedDates, setSelectedDates] = useState<DateRange>(
        initialFrom && initialTo ? { from: new Date(initialFrom), to: new Date(initialTo) } : resolveDefaultDates(initialTimezone),
    );
    const [selectedGranularity, setSelectedGranularity] = useState<StatGranularity>(initialGranularity);
    const [selectedStores, setSelectedStores] = useState<string[]>(initialStores);
    const [selectedTimezone, setSelectedTimezone] = useState<string>(initialTimezone);
    const [selectedCurrency, setSelectedCurrency] = useState<string>(initialCurrency);

    // URL sync hook
    const { updateURL } = useUrlSync({
        selectedDates,
        selectedGranularity,
        selectedStores,
        selectedTimezone,
        selectedCurrency,
    });

    const selectDates = useCallback((dates: DateRange) => {
        if (!dates) return;

        const normalized = {
            from: createTimezoneAwareDate(dates.from, selectedTimezone),
            to: createTimezoneAwareDate(dates.to, selectedTimezone, true),
        };
        setSelectedDates(normalized);
        updateURL({ selectedDates: normalized });
    }, [updateURL, selectedTimezone]);

    const selectGranularity = useCallback((value: StatGranularity) => {
        setSelectedGranularity(value);
        updateURL({ selectedGranularity: value });
    }, [updateURL]);

    const selectStores = useCallback((stores: string[]) => {
        setSelectedStores(stores);
        updateURL({ selectedStores: stores });
    }, [updateURL]);

    const selectTimezone = useCallback((timezone: string) => {
        setSelectedTimezone(timezone);

        const newDates = {
            from: createTimezoneAwareDate(selectedDates.from, timezone),
            to: createTimezoneAwareDate(selectedDates.to, timezone, true),
        };
        setSelectedDates(newDates);
        updateURL({ selectedTimezone: timezone, selectedDates: newDates });
    }, [updateURL, selectedDates]);

    const selectCurrency = useCallback((currency: string) => {
        setSelectedCurrency(currency);
        updateURL({ selectedCurrency: currency });
    }, [updateURL]);

    const granularities = useMemo(() => {
        const days = differenceInDays(selectedDates.to, selectedDates.from);
        const list: StatGranularity[] = ['daily'];
        if (days > 14) list.push('weekly');
        if (days > 40) list.push('monthly');
        return list;
    }, [selectedDates]);

    // Auto-reset granularity if it becomes invalid due to date range change
    useEffect(() => {
        if (!granularities.includes(selectedGranularity)) {
            setSelectedGranularity('daily');
            updateURL({ selectedGranularity: 'daily' });
        }
    }, [granularities, selectedGranularity, updateURL]);

    // Snap the timezone default to accounts.timezone once loaded, but only
    // when the URL didn't explicitly carry a timezone — respecting a shared
    // link stays the priority. Fires at most once per URL state.
    useEffect(() => {
        if (urlTimezone) return;
        if (!isAccountTimezoneReady) return;
        if (accountTimezone === selectedTimezone) return;
        selectTimezone(accountTimezone);
    }, [urlTimezone, isAccountTimezoneReady, accountTimezone, selectedTimezone, selectTimezone]);

    const datePresets = useMemo(() => {
        return getDatePresetsForTimezone(selectedTimezone);
    }, [selectedTimezone]);

    // Fetch currencies from account filtered by date range
    const { data: currencyData } = trpc.analytics.getPaymentCurrencies.useQuery(
        {
            from: selectedDates.from.toISOString(),
            to: selectedDates.to.toISOString(),
        },
        {
            enabled: !!selectedDates.from && !!selectedDates.to,
        }
    );

    const currencies = useMemo(
        () => (currencyData || []).map((row: any) => row.currency).filter((c: string) => c !== null && c !== undefined),
        [currencyData],
    );

    // Auto-select currency when currencies are loaded
    useEffect(() => {
        if (currencies.length > 0 && !currencies.includes(selectedCurrency)) {
            const defaultCurrency = currencies[0];
            setSelectedCurrency(defaultCurrency);
            updateURL({ selectedCurrency: defaultCurrency });
        }
    }, [currencies, selectedCurrency, updateURL]);

    const value = useMemo(() => ({
        selectedDates,
        selectDates,
        granularities,
        selectedGranularity,
        selectGranularity,
        selectedStores,
        selectStores,
        selectedTimezone,
        selectTimezone,
        selectedCurrency,
        selectCurrency,
        currencies,
        datePresets,
    }), [
        selectedDates,
        selectDates,
        granularities,
        selectedGranularity,
        selectGranularity,
        selectedStores,
        selectStores,
        selectedTimezone,
        selectTimezone,
        selectedCurrency,
        selectCurrency,
        currencies,
        datePresets,
    ]);

    return (
        <StatsFiltersContext.Provider value={value}>
            {children}
        </StatsFiltersContext.Provider>
    );
};

export const useStatsFilters = () => useContext(StatsFiltersContext);

export { getPeriod } from './utils/timezoneUtils';


